import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, AuthRequest } from "../../lib";

/**
 * Advanced order search, ported from the legacy search screen.
 *
 * `col` is a span on a 12-column grid, so the fields line up in rows the way
 * the original did rather than wrapping wherever their content lands.
 */
const FIELDS = [
  { name: "order_id1", label: "Order No. From", col: 2, type: "number" },
  { name: "order_id2", label: "Order No. To", col: 2, type: "number" },
  { name: "datefrom", label: "Ordered From", col: 3, type: "date" },
  { name: "dateto", label: "Ordered To", col: 3, type: "date" },
  // Disabled for now: the values are analytics-derived (set_source writes
  // whatever the referrer sent), so the list needs its own considered
  // treatment. get_sources() is just SELECT DISTINCT source FROM `order`.
  { name: "source", label: "Source", col: 2, disabled: true },

  { name: "name", label: "Name", col: 4 },
  { name: "billemail", label: "Email", col: 4 },
  { name: "billphone", label: "Phone", col: 2 },
  { name: "ip", label: "IP", col: 2 },

  { name: "street", label: "Street Address", col: 4 },
  { name: "city", label: "City", col: 3 },
  { name: "state", label: "State", col: 2 },
  { name: "zip", label: "Zip", col: 2 },
  { name: "location", label: "Country", col: 1 },

  { name: "brand", label: "Brand", col: 3 },
  { name: "model", label: "Model Number", col: 3 },
  { name: "paymethod", label: "Payment Method", col: 2, type: "payMethod" },
  { name: "status_id", label: "Status", col: 2, type: "status" },
  { name: "order_type", label: "Order Type", col: 2, type: "orderType" },
];

const REPORTS = [
  { type: "all", label: "All Orders" },
  { type: "on-special", label: "On Special" },
  { type: "sales-tax", label: "Sales Tax" },
];

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1100, margin: "24px auto 0", width: "100%" },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  title: { fontSize: 19, fontWeight: 500 },
  spacer: { flex: 1 },
  panel: { padding: theme.spacing(2.5) },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gap: theme.spacing(2),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2.5),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid rgba(0,0,0,0.10)",
  },
  hint: { color: theme.palette.text.secondary, fontSize: 12.5 },
  reports: { marginTop: theme.spacing(2) },
  reportRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1.5) },
}));

export function OrderSearchPage() {
  const classes = useStyles();
  const history = useHistory();

  const [form, setForm] = useState({});
  const [range, setRange] = useState({ start: "", end: "" });
  const [busy, setBusy] = useState("");
  const [reportError, setReportError] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [payMethods, setPayMethods] = useState([]);

  useEffect(() => {
    authGet("/orders/lookups").then(({ data }) => {
      setStatuses(data?.statuses ?? []);
      setPayMethods(data?.payMethods ?? []);
    });
  }, []);

  const set = (name) => (e) => setForm((s) => ({ ...s, [name]: e.target.value }));

  /**
   * The report endpoint returns a file, so it cannot be a plain link — the
   * request needs the auth header. Fetch it as a blob and hand the browser a
   * temporary object URL to save.
   */
  const download = async (type) => {
    setBusy(type);
    setReportError("");
    try {
      const res = await AuthRequest.get(
        `/orders/reports/${type}?start=${range.start}&end=${range.end}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orders-${type}-${range.start}-to-${range.end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // The error body arrives as a blob too, so it has to be read back.
      let message = err?.message || "Could not build that report.";
      const body = err?.response?.data;
      if (body instanceof Blob) {
        try {
          message = JSON.parse(await body.text())?.message || message;
        } catch (_) {
          /* keep the generic message */
        }
      }
      setReportError(message);
    }
    setBusy("");
  };

  const submit = (e) => {
    e.preventDefault();
    // Blank fields are not criteria, so they never reach the URL.
    const params = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== undefined && String(v).trim() !== "") params.set(k, String(v).trim());
    });
    history.push(`/orders?${params.toString()}`);
  };

  const options = {
    status: [{ value: "", label: "Any" }].concat(
      statuses.map((s) => ({ value: String(s.status_id), label: s.status })),
    ),
    payMethod: [{ value: "", label: "Any" }].concat(
      payMethods.map((p) => ({ value: String(p.pay_method_id), label: p.pay_method })),
    ),
    orderType: [
      { value: "", label: "Any" },
      { value: "new", label: "New" },
      { value: "preowned", label: "Pre-Owned" },
    ],
    // The legacy screen filled this from order.get_sources(); the column is
    // free text, so it is typed rather than picked until that list exists.
    source: null,
  };

  return (
    <Box className={classes.page}>
      <form onSubmit={submit}>
        <div className={classes.head}>
          <Button size="small" variant="outlined" onClick={() => history.push("/orders")}>
            ← Orders
          </Button>
          <Typography className={classes.title}>Search Orders</Typography>
          <div className={classes.spacer} />
        </div>

        <Paper className={classes.panel}>
          <div className={classes.grid}>
            {FIELDS.map((f) => {
              const opts = options[f.type];
              const style = { gridColumn: `span ${f.col}` };

              if (opts) {
                return (
                  <TextField
                    key={f.name}
                    select
                    fullWidth
                    label={f.label}
                    style={style}
                    value={form[f.name] ?? ""}
                    onChange={set(f.name)}
                  >
                    {opts.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }

              return (
                <TextField
                  key={f.name}
                  fullWidth
                  disabled={!!f.disabled}
                  helperText={f.disabled ? "Not yet available" : undefined}
                  label={f.label}
                  type={f.type === "date" || f.type === "number" ? f.type : "text"}
                  style={style}
                  value={form[f.name] ?? ""}
                  onChange={set(f.name)}
                />
              );
            })}
          </div>

          <div className={classes.actions}>
            <Button type="submit" variant="contained" color="primary" size="small">
              Search Orders
            </Button>
            <Button size="small" onClick={() => setForm({})}>
              Clear
            </Button>
          </div>
        </Paper>
      </form>

      <Paper className={`${classes.panel} ${classes.reports}`}>
        <Typography className={classes.title}>Reports</Typography>
        <div className={classes.hint}>
          Downloads a CSV for the date range. Orders are ranged on order date,
          except the sales-tax report which uses ship date.
        </div>

        <div className={classes.reportRow}>
          <TextField
            label="From"
            type="date"
            value={range.start}
            onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
          />
          <TextField
            label="To"
            type="date"
            value={range.end}
            onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
          />
          {REPORTS.map((r) => (
            <Button
              key={r.type}
              size="small"
              variant="outlined"
              disabled={busy !== "" || !range.start || !range.end}
              onClick={() => download(r.type)}
            >
              {busy === r.type ? "Building…" : r.label}
            </Button>
          ))}
        </div>

        {reportError && <div className={classes.error}>{reportError}</div>}
      </Paper>
    </Box>
  );
}

export default OrderSearchPage;
